import React, { useState, useEffect } from 'react';
import { 
  Crown, Users, Briefcase, TrendingUp, TrendingDown, AlertCircle, 
  Clock, Target, Award, CheckCircle, XCircle, Filter, Search, 
  RefreshCw, Download, Calendar, PieChart, Activity,
  Eye, Edit, MessageSquare, ChevronDown, ChevronRight, Star,
  UserCheck, Zap, FileText, Send, DollarSign, Building2, UserCircle,
  X, MapPin, Info, Sparkles
} from 'lucide-react';
import toast from 'react-hot-toast';
import VendorManagement from './VendorManagement';
import Submissions from './Submissions';
import LeadCandidateManagement from '../components/LeadCandidateManagement';
import AddSubmissionModal from '../components/AddSubmissionModal';
import EnhancedCandidateCreateModal from '../components/EnhancedCandidateCreateModal';
import AISourcing from './AISourcing';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Submission stages for pipeline tracking
const PIPELINE_STAGES = [
  { key: 'sourced', label: 'Sourced', color: 'bg-gray-100 text-gray-800' },
  { key: 'screened', label: 'Screened', color: 'bg-blue-100 text-blue-800' },
  { key: 'internally_submitted', label: 'Submitted', color: 'bg-purple-100 text-purple-800' },
  { key: 'client_reviewing', label: 'Client Review', color: 'bg-yellow-100 text-yellow-800' },
  { key: 'interview_scheduled', label: 'Interview', color: 'bg-indigo-100 text-indigo-800' },
  { key: 'offer_made', label: 'Offer', color: 'bg-orange-100 text-orange-800' },
  { key: 'offer_accepted', label: 'Accepted', color: 'bg-green-100 text-green-800' },
];

const LeadLenaDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  
  // Data states
  const [recruiters, setRecruiters] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  
  // Submission modal states
  const [showAddSubmissionModal, setShowAddSubmissionModal] = useState(false);
  const [selectedJobForSubmission, setSelectedJobForSubmission] = useState(null);
  const [showAddCandidateModal, setShowAddCandidateModal] = useState(false);
  
  // Filter states
  const [filters, setFilters] = useState({
    client: 'all',
    recruiter: 'all',
    reqStatus: 'all',
    candidateStatus: 'all',
    location: 'all',
    priority: 'all',
    dateRange: 'last_30_days',
    searchTerm: ''
  });

  // Metrics states
  const [teamMetrics, setTeamMetrics] = useState({
    totalRecruiters: 0,
    activeCandidates: 0,
    activeSubmissions: 0,
    openJobs: 0,
    avgTimeToSubmit: 0,
    avgTimeToFill: 0,
    interviewConversionRate: 0,
    offerAcceptanceRate: 0,
    thisWeekSubmissions: 0,
    thisWeekOffers: 0,
    slaBreaches: 0
  });

  const [recruiterPerformance, setRecruiterPerformance] = useState([]);
  const [pipelineHealth, setPipelineHealth] = useState({});

  useEffect(() => {
    fetchAllData();
  }, []);

  useEffect(() => {
    calculateMetrics();
  }, [recruiters, candidates, submissions, jobs]);

  // Clear search terms when switching tabs
  useEffect(() => {
    setFilters(prev => ({ ...prev, searchTerm: '' }));
  }, [activeTab]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchRecruiters(),
        fetchCandidates(),
        fetchSubmissions(),
        fetchJobs(),
        fetchCustomers()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchRecruiters = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/users`);
      if (response.ok) {
        const data = await response.json();
        const recruiterUsers = (data.users || []).filter(u => 
          u.role === 'recruiter' || u.role === 'lead'
        );
        setRecruiters(recruiterUsers);
      }
    } catch (error) {
      console.error('Error fetching recruiters:', error);
    }
  };

  const fetchCandidates = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/candidates`);
      if (response.ok) {
        const data = await response.json();
        // API returns array directly
        const candidates = Array.isArray(data) ? data : (data.candidates || []);
        setCandidates(candidates);
      }
    } catch (error) {
      console.error('Error fetching candidates:', error);
    }
  };

  const fetchSubmissions = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/submissions`);
      if (response.ok) {
        const data = await response.json();
        setSubmissions(data.submissions || []);
      }
    } catch (error) {
      console.error('Error fetching submissions:', error);
    }
  };

  const fetchJobs = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/jobs`);
      if (response.ok) {
        const data = await response.json();
        // API returns array directly
        const jobs = Array.isArray(data) ? data : (data.jobs || []);
        setJobs(jobs);
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/customers`);
      if (response.ok) {
        const data = await response.json();
        setCustomers(data.customers || []);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const calculateMetrics = () => {
    // Team-level metrics
    const activeCandidates = candidates.filter(c => c.candidate_status === 'active').length;
    const activeSubmissions = submissions.filter(s => 
      !['offer_accepted', 'rejected', 'internally_rejected'].includes(s.current_status)
    ).length;
    const openJobs = jobs.filter(j => j.status === 'open').length;

    // Time-based metrics (simplified calculations)
    const submissionsWithDates = submissions.filter(s => s.submission_date && s.created_at);
    const avgTimeToSubmit = submissionsWithDates.length > 0
      ? submissionsWithDates.reduce((acc, s) => {
          const days = Math.floor((new Date(s.submission_date) - new Date(s.created_at)) / (1000 * 60 * 60 * 24));
          return acc + days;
        }, 0) / submissionsWithDates.length
      : 0;

    // Conversion rates
    const interviews = submissions.filter(s => 
      ['interview_scheduled', 'interview_scheduled', 'interview_scheduled'].includes(s.current_status)
    ).length;
    const offers = submissions.filter(s => s.current_status === 'offer_made').length;
    const accepted = submissions.filter(s => s.current_status === 'offer_accepted').length;
    
    const interviewConversionRate = activeSubmissions > 0 
      ? ((interviews / activeSubmissions) * 100).toFixed(1)
      : 0;
    const offerAcceptanceRate = offers > 0
      ? ((accepted / offers) * 100).toFixed(1)
      : 0;

    // This week metrics
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
    const thisWeekSubmissions = submissions.filter(s => 
      new Date(s.submission_date) > oneWeekAgo
    ).length;
    const thisWeekOffers = submissions.filter(s => 
      s.current_status === 'offer_made' && new Date(s.updated_at) > oneWeekAgo
    ).length;

    setTeamMetrics({
      totalRecruiters: recruiters.length,
      activeCandidates,
      activeSubmissions,
      openJobs,
      avgTimeToSubmit: Math.round(avgTimeToSubmit),
      avgTimeToFill: 0, // Would need job fill dates
      interviewConversionRate,
      offerAcceptanceRate,
      thisWeekSubmissions,
      thisWeekOffers,
      slaBreaches: 0 // Would need SLA definitions
    });

    // Calculate recruiter performance
    calculateRecruiterPerformance();
    calculatePipelineHealth();
  };

  const calculateRecruiterPerformance = () => {
    const performance = recruiters.map(recruiter => {
      const recruiterCandidates = candidates.filter(c => 
        c.primary_recruiter_id === recruiter.id || c.created_by === recruiter.id
      );
      const recruiterSubmissions = submissions.filter(s => 
        s.recruiter_id === recruiter.id || s.submitted_by_id === recruiter.id
      );
      
      const activeSubmissions = recruiterSubmissions.filter(s => 
        !['offer_accepted', 'rejected', 'internally_rejected'].includes(s.current_status)
      ).length;

      const interviews = recruiterSubmissions.filter(s => 
        ['interview_scheduled', 'interview_scheduled', 'interview_scheduled'].includes(s.current_status)
      ).length;

      const offers = recruiterSubmissions.filter(s => 
        s.current_status === 'offer_made' || s.current_status === 'offer_accepted'
      ).length;

      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
      const recentActivity = recruiterSubmissions.filter(s => 
        new Date(s.submission_date) > oneWeekAgo
      ).length;

      return {
        id: recruiter.id,
        name: recruiter.full_name || recruiter.username,
        email: recruiter.email,
        candidates: recruiterCandidates.length,
        submissions: recruiterSubmissions.length,
        activeSubmissions,
        interviews,
        offers,
        recentActivity,
        conversionRate: activeSubmissions > 0 
          ? ((interviews / activeSubmissions) * 100).toFixed(1)
          : 0
      };
    });

    setRecruiterPerformance(performance.sort((a, b) => b.submissions - a.submissions));
  };

  const calculatePipelineHealth = () => {
    const health = {};
    PIPELINE_STAGES.forEach(stage => {
      health[stage.key] = submissions.filter(s => s.current_status === stage.key).length;
    });
    setPipelineHealth(health);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Overview Tab
  const OverviewTab = () => {
    const [selectedMetric, setSelectedMetric] = useState(null);

    // Filter data based on selected metric
    const getFilteredData = () => {
      switch(selectedMetric) {
        case 'submissions':
          return submissions.filter(s => s.status !== 'rejected' && s.status !== 'offer_accepted');
        case 'jobs':
          return jobs;
        case 'interviews':
          return submissions.filter(s => s.status === 'interview_scheduled');
        case 'offers':
          return submissions.filter(s => s.status === 'offer_made' || s.status === 'offer_extended');
        case 'onboards':
          return submissions.filter(s => s.status === 'offer_accepted' || s.status === 'onboarding');
        default:
          return [];
      }
    };

    const filteredData = getFilteredData();

    return (
      <div className="space-y-6">
        {/* Stats Cards - Clickable */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div 
            onClick={() => setSelectedMetric('submissions')}
            className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-6 text-white cursor-pointer hover:shadow-xl hover:scale-105 transition-all"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm font-medium">Current Open Submissions</p>
                <p className="text-3xl font-bold mt-1">{teamMetrics.activeSubmissions}</p>
              </div>
              <Send className="h-12 w-12 text-purple-200" />
            </div>
          </div>

          <div 
            onClick={() => setSelectedMetric('jobs')}
            className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg p-6 text-white cursor-pointer hover:shadow-xl hover:scale-105 transition-all"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-100 text-sm font-medium">Current Open Jobs</p>
                <p className="text-3xl font-bold mt-1">{teamMetrics.openJobs}</p>
              </div>
              <Briefcase className="h-12 w-12 text-orange-200" />
            </div>
          </div>

          <div 
            onClick={() => setSelectedMetric('interviews')}
            className="bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg p-6 text-white cursor-pointer hover:shadow-xl hover:scale-105 transition-all"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-indigo-100 text-sm font-medium">Current Interviews</p>
                <p className="text-3xl font-bold mt-1">
                  {submissions.filter(s => s.status === 'interview_scheduled').length}
                </p>
              </div>
              <Calendar className="h-12 w-12 text-indigo-200" />
            </div>
          </div>

          <div 
            onClick={() => setSelectedMetric('offers')}
            className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-6 text-white cursor-pointer hover:shadow-xl hover:scale-105 transition-all"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm font-medium">Current Offers in Progress</p>
                <p className="text-3xl font-bold mt-1">
                  {submissions.filter(s => s.status === 'offer_made' || s.status === 'offer_extended').length}
                </p>
              </div>
              <Award className="h-12 w-12 text-green-200" />
            </div>
          </div>

          <div 
            onClick={() => setSelectedMetric('onboards')}
            className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-6 text-white cursor-pointer hover:shadow-xl hover:scale-105 transition-all"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm font-medium">Current Onboards in Progress</p>
                <p className="text-3xl font-bold mt-1">
                  {submissions.filter(s => s.status === 'offer_accepted' || s.status === 'onboarding').length}
                </p>
              </div>
              <UserCheck className="h-12 w-12 text-blue-200" />
            </div>
          </div>
        </div>

        {/* Data Table - Shows when metric is clicked */}
        {selectedMetric && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                {selectedMetric === 'submissions' && 'Open Submissions'}
                {selectedMetric === 'jobs' && 'Open Jobs'}
                {selectedMetric === 'interviews' && 'Candidates in Interviews'}
                {selectedMetric === 'offers' && 'Offers in Progress'}
                {selectedMetric === 'onboards' && 'Onboarding in Progress'}
              </h3>
              <button
                onClick={() => setSelectedMetric(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="overflow-x-auto">
              {selectedMetric === 'jobs' ? (
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Job Title</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assigned Recruiter</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Submissions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredData.map(job => (
                      <tr key={job.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{job.title}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{job.client || job.client_name || 'N/A'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{job.location || 'Remote'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{job.assigned_recruiter || 'Unassigned'}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                            {job.status || 'Open'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {submissions.filter(s => s.job_id === job.id).length}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Candidate</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Job</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Recruiter</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Submitted Date</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredData.map(submission => (
                      <tr key={submission.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {submission.candidate_name || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {submission.job_title || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {submission.submitted_by_name || submission.recruiter_name || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                            {(submission.current_status || submission.status)?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'N/A'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {submission.submitted_at ? formatDate(submission.submitted_at) : 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            {filteredData.length === 0 && (
              <div className="p-12 text-center">
                <p className="text-gray-500">No data available</p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Recruiter Performance Tab
  const RecruiterPerformanceTab = () => (
    <div className="space-y-4">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Recruiter</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Candidates</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Submissions</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Active</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Interviews</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Offers</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Conversion %</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">This Week</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {recruiterPerformance.map(recruiter => (
              <tr key={recruiter.id} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  <div>
                    <p className="font-medium text-gray-900">{recruiter.name}</p>
                    <p className="text-sm text-gray-500">{recruiter.email}</p>
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">{recruiter.candidates}</td>
                <td className="px-6 py-4 text-sm text-gray-900">{recruiter.submissions}</td>
                <td className="px-6 py-4 text-sm text-gray-900">{recruiter.activeSubmissions}</td>
                <td className="px-6 py-4 text-sm text-gray-900">{recruiter.interviews}</td>
                <td className="px-6 py-4 text-sm text-gray-900">{recruiter.offers}</td>
                <td className="px-6 py-4">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    parseFloat(recruiter.conversionRate) > 50 
                      ? 'bg-green-100 text-green-800'
                      : parseFloat(recruiter.conversionRate) > 30
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {recruiter.conversionRate}%
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">{recruiter.recentActivity}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  // Open Jobs Tab - Tabular View for all team recruiters
  const OpenJobsTab = () => {
    const [selectedJob, setSelectedJob] = useState(null);
    const [showJobDetailModal, setShowJobDetailModal] = useState(false);
    const [jobSearchTerm, setJobSearchTerm] = useState('');
    
    const filteredJobs = jobs.filter(j =>
      j.title?.toLowerCase().includes(jobSearchTerm.toLowerCase()) ||
      j.company?.toLowerCase().includes(jobSearchTerm.toLowerCase()) ||
      j.location?.toLowerCase().includes(jobSearchTerm.toLowerCase())
    );

    const handleJobClick = (job) => {
      setSelectedJob(job);
      setShowJobDetailModal(true);
    };

    return (
      <div className="space-y-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-gray-600">
              Showing {filteredJobs.length} open jobs for your team
            </p>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search jobs by title, company, or location..."
              value={jobSearchTerm}
              onChange={(e) => setJobSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Tabular View */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Job Title
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Company
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Location
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Assigned Recruiter
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredJobs.map(job => (
                  <tr 
                    key={job.id}
                    onClick={() => handleJobClick(job)}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Briefcase className="h-5 w-5 text-purple-600 mr-3" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">{job.title}</div>
                          {job.department && (
                            <div className="text-xs text-gray-500">{job.department}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{job.company || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{job.location || 'Remote'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                        {job.employment_type || 'Full-time'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        job.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {job.status || 'Active'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{job.assigned_recruiter || 'Unassigned'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleJobClick(job);
                        }}
                        className="text-purple-600 hover:text-purple-900 flex items-center gap-1"
                      >
                        <Eye className="h-4 w-4" />
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredJobs.length === 0 && (
            <div className="p-12 text-center">
              <Briefcase className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No open jobs found</p>
            </div>
          )}
        </div>

        {/* Job Detail Modal */}
        {showJobDetailModal && selectedJob && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              {/* Header */}
              <div className="bg-gradient-to-r from-purple-600 to-blue-600 px-6 py-4 flex justify-between items-start">
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-white mb-1">{selectedJob.title}</h2>
                  <p className="text-purple-100">{selectedJob.company}</p>
                </div>
                <button
                  onClick={() => {
                    setShowJobDetailModal(false);
                    setSelectedJob(null);
                  }}
                  className="text-white hover:text-gray-200"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
                <div className="space-y-6">
                  {/* Key Decision-Making Information - Highlighted Section */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h3 className="text-sm font-semibold text-blue-900 mb-3 flex items-center gap-2">
                      <Info className="h-4 w-4" />
                      Key Information
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-xs text-blue-600 font-medium mb-1">Client</p>
                        <p className="text-sm font-semibold text-blue-900">
                          {selectedJob.client || selectedJob.client_name || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-blue-600 font-medium mb-1">Job Opened</p>
                        <p className="text-sm font-semibold text-blue-900">
                          {selectedJob.created_at ? formatDate(selectedJob.created_at) : 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-blue-600 font-medium mb-1">Current Submissions</p>
                        <p className="text-sm font-semibold text-blue-900">
                          {submissions.filter(s => s.job_id === selectedJob.id).length} candidates
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-blue-600 font-medium mb-1">Sales Manager</p>
                        <p className="text-sm font-semibold text-blue-900">
                          {selectedJob.account_manager || selectedJob.hiring_manager || 'N/A'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Quick Info */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="flex items-center gap-2 text-sm">
                      <MapPin className="h-4 w-4 text-gray-400" />
                      <span>{selectedJob.location || 'Remote'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Briefcase className="h-4 w-4 text-gray-400" />
                      <span>{selectedJob.employment_type || 'Full-time'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Users className="h-4 w-4 text-gray-400" />
                      <span>{selectedJob.no_of_openings || 1} {selectedJob.no_of_openings === 1 ? 'opening' : 'openings'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span className="text-green-600 font-medium">{selectedJob.status || 'Active'}</span>
                    </div>
                  </div>

                  {/* Assigned Recruiter */}
                  {selectedJob.assigned_recruiter && (
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                      <div className="flex items-center gap-2">
                        <UserCheck className="h-5 w-5 text-purple-600" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">Assigned Recruiter</p>
                          <p className="text-sm text-purple-600">{selectedJob.assigned_recruiter}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Job Description */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Job Description</h3>
                    <div className="prose prose-sm max-w-none text-gray-600">
                      {selectedJob.description || 'No description available.'}
                    </div>
                  </div>

                  {/* Requirements */}
                  {selectedJob.requirements && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Requirements</h3>
                      <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
                        {(Array.isArray(selectedJob.requirements) 
                          ? selectedJob.requirements 
                          : selectedJob.requirements.split('\n')
                        ).map((req, idx) => (
                          <li key={idx}>{req}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Skills */}
                  {selectedJob.required_skills && selectedJob.required_skills.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Required Skills</h3>
                      <div className="flex flex-wrap gap-2">
                        {selectedJob.required_skills.map((skill, idx) => (
                          <span 
                            key={idx}
                            className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-medium"
                          >
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Compensation */}
                  {(selectedJob.salary_range_min || selectedJob.salary_range_max) && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Compensation</h3>
                      <p className="text-gray-600">
                        ${selectedJob.salary_range_min?.toLocaleString()} - ${selectedJob.salary_range_max?.toLocaleString()}
                        {selectedJob.salary_currency && ` ${selectedJob.salary_currency}`}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Footer Actions */}
              <div className="bg-gray-50 px-6 py-4 flex justify-between items-center border-t">
                <button
                  onClick={() => {
                    setShowJobDetailModal(false);
                    setSelectedJob(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    setSelectedJobForSubmission(selectedJob);
                    setShowJobDetailModal(false);
                    setShowAddSubmissionModal(true);
                  }}
                  className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
                >
                  <Send className="h-4 w-4" />
                  Submit Candidate to this Role
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // REMOVED: Pipeline View Tab and Workload View Tab
  // These tabs have been removed from the navigation per user request
  /*
  // Pipeline View Tab
  const PipelineViewTab = () => {
    const groupedByStage = {};
    submissions.forEach(sub => {
      if (!groupedByStage[sub.current_status]) {
        groupedByStage[sub.current_status] = [];
      }
      groupedByStage[sub.current_status].push(sub);
    });

    return (
      <div className="space-y-6">
        {PIPELINE_STAGES.map(stage => {
          const stageSubmissions = groupedByStage[stage.key] || [];
          return (
            <div key={stage.key} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className={`${stage.color} px-6 py-4 border-b border-gray-200`}>
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-lg">{stage.label}</h3>
                  <span className="px-3 py-1 bg-white rounded-full text-sm font-medium">
                    {stageSubmissions.length} submissions
                  </span>
                </div>
              </div>
              {stageSubmissions.length > 0 ? (
                <div className="divide-y divide-gray-200">
                  {stageSubmissions.slice(0, 5).map(submission => (
                    <div key={submission.id} className="px-6 py-4 hover:bg-gray-50 flex items-center justify-between">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{submission.candidate_name}</p>
                        <p className="text-sm text-gray-500">{submission.job_title} • {submission.client_name}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          By {submission.recruiter_name} • {formatDate(submission.submission_date)}
                        </p>
                      </div>
                      <button className="text-blue-600 hover:text-blue-700">
                        <Eye className="h-5 w-5" />
                      </button>
                    </div>
                  ))}
                  {stageSubmissions.length > 5 && (
                    <div className="px-6 py-3 bg-gray-50 text-center text-sm text-gray-500">
                      +{stageSubmissions.length - 5} more submissions
                    </div>
                  )}
                </div>
              ) : (
                <div className="px-6 py-8 text-center text-gray-500">
                  No submissions in this stage
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Workload View Tab
  const WorkloadViewTab = () => (
    <div className="space-y-4">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Recruiter Workload Distribution</h3>
        <div className="space-y-4">
          {recruiterPerformance.map(recruiter => {
            const workloadPercentage = teamMetrics.activeCandidates > 0
              ? (recruiter.candidates / teamMetrics.activeCandidates) * 100
              : 0;
            
            return (
              <div key={recruiter.id} className="space-y-2">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">{recruiter.name}</p>
                    <p className="text-sm text-gray-500">
                      {recruiter.candidates} candidates • {recruiter.activeSubmissions} active submissions
                    </p>
                  </div>
                  <span className="text-sm font-semibold text-gray-600">
                    {workloadPercentage.toFixed(1)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className="bg-purple-600 h-3 rounded-full transition-all"
                    style={{ width: `${workloadPercentage}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
  */

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <div className="h-12 w-12 bg-purple-600 rounded-full flex items-center justify-center text-white">
              <Crown className="h-6 w-6" />
            </div>
            Lead Lena Dashboard
          </h1>
          <p className="text-gray-600 mt-1">Supervise team performance and pipeline health</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Filter className="h-4 w-4" />
            Filters
          </button>
          <button
            onClick={fetchAllData}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Global Filters */}
      {showFilters && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Global Filters</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  value={filters.searchTerm}
                  onChange={(e) => setFilters({...filters, searchTerm: e.target.value})}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Client</label>
              <select 
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                value={filters.client}
                onChange={(e) => setFilters({...filters, client: e.target.value})}
              >
                <option value="all">All Clients</option>
                {customers.map(c => (
                  <option key={c.id} value={c.id}>{c.company_name || c.client_name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Recruiter</label>
              <select 
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                value={filters.recruiter}
                onChange={(e) => setFilters({...filters, recruiter: e.target.value})}
              >
                <option value="all">All Recruiters</option>
                {recruiters.map(r => (
                  <option key={r.id} value={r.id}>{r.full_name || r.username}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
              <select 
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                value={filters.dateRange}
                onChange={(e) => setFilters({...filters, dateRange: e.target.value})}
              >
                <option value="last_7_days">Last 7 Days</option>
                <option value="last_30_days">Last 30 Days</option>
                <option value="last_90_days">Last 90 Days</option>
                <option value="this_month">This Month</option>
                <option value="this_quarter">This Quarter</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px overflow-x-auto">
            {[
              { key: 'overview', label: 'Overview', icon: Activity },
              { key: 'candidate-mgmt', label: 'Candidate Management', icon: UserCircle },
              { key: 'ai-sourcing', label: 'AI Sourcing', icon: Sparkles },
              { key: 'jobs', label: 'Open Jobs', icon: Briefcase },
              { key: 'submission-mgmt', label: 'Client Submissions', icon: Send },
              { key: 'performance', label: 'Recruiter Performance', icon: TrendingUp },
              { key: 'vendor-mgmt', label: 'Vendor Management', icon: Building2 }
            ].map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-2 px-6 py-4 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                    activeTab === tab.key
                      ? 'border-purple-600 text-purple-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 text-gray-400 animate-spin" />
            </div>
          ) : (
            <>
              {activeTab === 'overview' && <OverviewTab />}
              {activeTab === 'ai-sourcing' && <AISourcing />}
              {activeTab === 'performance' && <RecruiterPerformanceTab />}
              {activeTab === 'jobs' && <OpenJobsTab />}
              {activeTab === 'candidate-mgmt' && (
                <LeadCandidateManagement 
                  onAddCandidate={() => setShowAddCandidateModal(true)}
                  onCandidateUpdated={fetchCandidates}
                />
              )}
              {activeTab === 'vendor-mgmt' && <VendorManagement />}
              {activeTab === 'submission-mgmt' && <Submissions />}
            </>
          )}
        </div>
      </div>

      {/* Add Submission Modal */}
      {showAddSubmissionModal && (
        <AddSubmissionModal
          isOpen={showAddSubmissionModal}
          onClose={() => {
            setShowAddSubmissionModal(false);
            setSelectedJobForSubmission(null);
          }}
          selectedJob={selectedJobForSubmission}
          onSubmissionCreated={() => {
            setShowAddSubmissionModal(false);
            setSelectedJobForSubmission(null);
            fetchAllData();
            toast.success('Candidate submitted successfully!');
          }}
        />
      )}

      {/* Add Candidate Modal */}
      {showAddCandidateModal && (
        <EnhancedCandidateCreateModal
          isOpen={showAddCandidateModal}
          onClose={() => setShowAddCandidateModal(false)}
          onCandidateCreated={() => {
            setShowAddCandidateModal(false);
            fetchCandidates();
            toast.success('Candidate added successfully!');
          }}
        />
      )}
    </div>
  );
};

// Metric Card Component
const MetricCard = ({ title, value, icon: Icon, color, trend, subtitle }) => {
  const colorClasses = {
    blue: 'from-blue-500 to-blue-600',
    purple: 'from-purple-500 to-purple-600',
    indigo: 'from-indigo-500 to-indigo-600',
    green: 'from-green-500 to-green-600',
    orange: 'from-orange-500 to-orange-600',
    teal: 'from-teal-500 to-teal-600',
    yellow: 'from-yellow-500 to-yellow-600',
    pink: 'from-pink-500 to-pink-600'
  };

  return (
    <div className={`bg-gradient-to-br ${colorClasses[color]} rounded-lg p-6 text-white`}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-white text-opacity-90 text-sm font-medium">{title}</p>
        <Icon className="h-8 w-8 text-white text-opacity-80" />
      </div>
      <p className="text-3xl font-bold">{value}</p>
      {subtitle && <p className="text-white text-opacity-75 text-xs mt-1">{subtitle}</p>}
      {trend && <p className="text-white text-opacity-90 text-sm mt-2">{trend}</p>}
    </div>
  );
};

export default LeadLenaDashboard;
